import type { ThemePreference } from '@/gql/graphql'

/**
 * Which palette to paint.
 *
 * The account's preference is the truth and lives on the server, so it follows
 * somebody between devices. This module owns the *browser* side of that: the
 * attribute on `<html>` that the tokens key off, and a localStorage mirror whose
 * only job is the first paint.
 *
 * The mirror is deliberately not a second source. Nothing reads it except the
 * inline script in `index.html`, which runs before the session has resolved and
 * would otherwise flash the wrong palette on every reload.
 */

export const THEME_KEY = 'nook.theme'

/** Written by `applyTheme`, read only by the inline script in index.html. */
export function mirrorTheme(preference: ThemePreference) {
  try {
    localStorage.setItem(THEME_KEY, preference.toLowerCase())
  } catch {
    // Private browsing. The cost is one frame of the wrong palette on reload.
  }
}

/**
 * Put the preference on the document.
 *
 * `SYSTEM` *removes* the attribute rather than writing "system", because the
 * stylesheet's rule is `:root:not([data-theme='light'])` inside a
 * `prefers-color-scheme` query — absence is what lets the media query win.
 */
export function applyTheme(preference: ThemePreference) {
  const root = document.documentElement

  if (preference === 'SYSTEM') root.removeAttribute('data-theme')
  else root.dataset.theme = preference.toLowerCase()

  // Keeps form controls, scrollbars and the caret in the same world as the rest
  // — they are painted by the browser, not by our tokens.
  root.style.colorScheme =
    preference === 'SYSTEM' ? 'light dark' : preference === 'DARK' ? 'dark' : 'light'

  mirrorTheme(preference)
  paintThemeColor()
}

/**
 * Keep the browser chrome the same colour as the app.
 *
 * Read from the live computed value rather than hardcoded, so it can never
 * disagree with `--bg` — which is exactly the kind of thing that rots when a
 * palette changes and a meta tag does not.
 */
export function paintThemeColor() {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) return
  meta.content = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim()
}
